//Contracts:
import { globals } from 'config/globals';
import { particles } from 'app/contracts/Particles';
import { Vector2 } from 'app/contracts/Geometry';
//Enums:
import { LookupType, RepellType, HomeParticleStateType } from 'app/enum/Particles';
//Library:
import { Particle } from 'particles/Particle';
import { ParticleProgressParallax } from 'particles/behaviour/ParticleProgressParallax';
import { ParticleMouseParallax } from 'particles/behaviour/ParticleMouseParallax';
import { ParticleRepell } from 'particles/behaviour/ParticleRepell';
import { ParticlePath } from 'particles/behaviour/ParticlePath';
import { ParticleCounter } from 'particles/behaviour/ParticleCounter';
import { ParticleHover } from 'particles/behaviour/ParticleHover';
import { ParticleDepth } from 'particles/behaviour/ParticleDepth';
import { ParticlePhysics } from 'particles/behaviour/ParticlePhysics';

import SpacesParticleProcessor from 'app/views/home/webgl/particles/perParticle/SpacesParticleProcessor';
import ScreensParticleProcessor from 'app/views/home/webgl/particles/perParticle/ScreensParticleProcessor';
import IconParticleProcessor from 'app/views/home/webgl/particles/perParticle/IconParticleProcessor';
import IntroParticleProcessor from 'app/views/home/webgl/particles/perParticle/IntroParticleProcessor';
import SystemsParticleProcessor from 'app/views/home/webgl/particles/perParticle/SystemsParticleProcessor';
import { ParticleTrailEmitter } from 'particles/behaviour/ParticleTrailEmitter';
import { ParticleTrail } from 'particles/behaviour/ParticleTrail';
import ParticleBehaviourLookup from 'particles/ParticleBehaviourLookup';

export class HomeParticle extends Particle {
	//Sub Processor to break up functionality
	public pooled: boolean = false;
	public spaces: SpacesParticleProcessor;
	public screens: ScreensParticleProcessor;
	public systems: SystemsParticleProcessor;
	public icon: IconParticleProcessor;
	public intro: IntroParticleProcessor;

	public repell: ParticleRepell;
	public orbitPath: ParticlePath;
	public iconPath: ParticlePath;
	public mouseParallax: ParticleMouseParallax;
	public particlePhysics: ParticlePhysics;
	public hover: ParticleHover;
	public counter: ParticleCounter;
	public counter2: ParticleCounter;
	public depth: ParticleDepth;
	public activeState: HomeParticleStateType;

	public emitter: ParticleTrailEmitter;

	//Transition/field-processor scratch state (written by TransitionParticles
	//and SpacesDepartment):
	public sineSeed: number;
	public moveSeed: number;
	public moveSpeed: number;
	public canDrawLine: boolean;

	//Saved targetAlpha/targetScale while a scene is hidden (see show()/hide()):
	private _ta: number;
	private _ts: number;
	//NOTE: written only in hideMe() and never read — possibly meant to be _ts.
	//Public only so noUnusedLocals doesn't flag the write-only field:
	public _s: number;

	private _trail: ParticleTrail;

	private _switching: string = null;

	constructor(sceneProps: particles.ParticlesSceneProps, container: PIXI.Container, config: particles.ParticleConfig) {
		super(sceneProps, container, config);

		this.spaces = new SpacesParticleProcessor(this, sceneProps);
		this.screens = new ScreensParticleProcessor(this, sceneProps);
		this.systems = new SystemsParticleProcessor(this, sceneProps);
		this.icon = new IconParticleProcessor(this, sceneProps);
		this.intro = new IntroParticleProcessor(this, sceneProps);
	}

	public get sceneProps() {
		return this._sceneProps;
	}

	public set index(n: number) {
		this._particleProps.sprite.name = 'Pool index ' + n;
	}

	public get position() {
		return this._position;
	}


	public get switching(): string {
		return this._switching;
	}

	public set switching(v: string) {
		this._switching = v;
	}

	/** After intro is complete then first 100ish particles are set to transition and stay in this state
	 *  When deep linking to screens/spaces they will be active straight away
	 */
	public transition() {}

	public changeParticle(type: string | null = null, zDepth = false) {
		if (!type) {
			let rand = Math.random();
			type = 'solid-ring.png';
			if (rand < 0.2) type = 'solid-square.png';
			else if (rand < 0.4) type = 'solid2.png';
		}

		this.particleProps.sprite.texture = this._sceneProps.textures[type];

		if (zDepth) {
			this.particleProps.sprite.zIndex = 100;
		}

		return type;
	}

	public resetParticle() {
		this.particleProps.sprite.texture = this._sceneProps.textures['solid2.png'];
	}

	//Particle is slideing from on scene to another
	public switchScenes(type): void {
		this.deactivateAllBehaviours(true);
		this._switching = type;
		this.active = true;
		this._startSceneSwitch(type);
	}

	public random(
		base: number = 0,
		seed: number = 1.0,
		miniusSeed: boolean = false,
		round: boolean = false,
		miniusAll: boolean = false
	): number {
		let d = miniusSeed && Math.random() < 0.5 ? -1 : 1;
		let num = base + Math.random() * (seed * d);

		if (miniusAll) {
			num *= Math.random() < 0.5 ? -1 : 1;
		}
		return round ? Math.round(num) : num;
	}

	public setMouseParallaxByScale(): void {
		this._animatedProps.setEase({
			x: this._animatedProps.targetScale * 100,
			y: this._animatedProps.targetScale * 50,
			scale: this._animatedProps.targetScale * 50
		});

		this.mouseParallax.setup({
			ease: { x: 20, y: 20 },
			power: { x: 0.1 * this._animatedProps.targetScale, y: 0.16 * this._animatedProps.targetScale }
		});
	}

	public tick(): void {
		if (!this._active || globals.skipNormalParticleUpdates || ParticleBehaviourLookup.paused) {
			return;
		}

		if (this.activeState === HomeParticleStateType.SPACES) this.spaces.tick();
		else if (this.activeState === HomeParticleStateType.SYSTEMS) this.systems.tick();

		super.tick();

	}

	public colourise(side: number, useRowIndex = false, useColIndex = false): void {}

	public startCounter(counter: ParticleCounter, frames: number, onTrigger: CallableFunction, loop: boolean = false) {
		counter.active = true;
		counter.triggerAtFrames = frames;
		counter.onTrigger = onTrigger;
		counter.loop = loop;
		counter.start();
	}

	public deactivate() {
		this.active = false;
		this.particleProps.sprite.visible = false;
	}

	public activate() {
		this.setActive(true);
		this.particleProps.sprite.visible = true;
	}

	/** Show particle as it's scene slides in  */
	public show(): void {
		this._animatedProps.targetAlpha = this._ta;
		this._animatedProps.scale = 0;
		this._animatedProps.targetScale = this._ts;
		this.hover.active = true;
	}

	/** Hide particle as its scene slides out  */
	public hide(): void {
		this.orbitPath.active = false;

		this._ta = this._animatedProps.targetAlpha;
		this._ts = this._animatedProps.targetScale;

		let flash = Math.random() < 0.07;
		flash = false;
		this._animatedProps.targetAlpha = 0;
		this._animatedProps.targetScale = flash ? 1 : 0;

		this._animatedProps.targetAlpha = 0;
		this._animatedProps.targetScale = 0;

		this.orbitPath.active = false;

		this._animatedProps.setEase({
			scale: 30,
			alpha: 30,
			x: 1,
			y: 1
		});
	}

	/** Hack version of hide. For masking. Trying to use visible but it doesn't work too well */
	public hideMe() {
		this.depth.active = false;
		this.particlePhysics.active = false;
		this.orbitPath.active = false;
		this.iconPath.active = false;
		this._animatedProps.targetAlpha = 0;
		this._s = 0;
	}

	public removeEase() {
		this._animatedProps.setEase({
			x: 1,
			y: 1
		});
	}

	public activateAsTrail(
		emitterParticleProps: particles.ParticleProps,
		emitterAnimatedProps: particles.ParticleProperties,
		lifetimeFramesBase: number,
		lifetimeFramesSeed: number,
		atPosition: Vector2 = null,
		inSafeZone: boolean = false,
		scaleOffParent: number = 0.35,
		colour = '#ffffff'
	): void {
		this.deactivateAllBehaviours(true);

		//Match the particle positiion to the

		this.activeState = HomeParticleStateType.SYSTEMS;
		this.activate();

		let spawnPos: Vector2 = atPosition;
		if (!spawnPos) {
			spawnPos = {
				x: emitterParticleProps.ref.position.x,
				y: emitterParticleProps.ref.position.y
			};
		}

		this.position.set(spawnPos, true);

		//Match the colour of the emitter:
		this.colour.set(colour);

		//Now configure the trail item
		this._trail.active = true;
		this._trail.alpha = inSafeZone ? emitterAnimatedProps.alpha * 0.4 : emitterAnimatedProps.alpha;
		this._trail.scale = emitterAnimatedProps.scale * scaleOffParent;

		const scaleLifetime = inSafeZone ? 0.2 : 1;
		this._trail.randomiseLifetime(lifetimeFramesBase * scaleLifetime, lifetimeFramesSeed * scaleLifetime);

		this.animatedProps.setVectorStyleMovement(true);

		this.systems.setSystemsStart();

		this.repell.target = this._sceneProps.targets.systemsMouse;
		//Allow for 150px circle and very tight ring:

		this.repell.active = inSafeZone ? false : true;
		this.repell.type = RepellType.PUSH;
		this.repell.radius = 50;
		this.repell.randomisePower(30, 200);

		this.changeParticle('solid2.png');

		//Allow for 150px circle and very tight ring:
		this.particlePhysics.active = false;

		this.position.active = true;
		this.position.disableTimelineX = true;

		this.orbitPath.active = false;
		this.position.active = true;
		this._animatedProps.setPosition = true;


		this._animatedProps.setEase({
			y: 2 + Math.random() * 2,
			x: 2 + Math.random() * 2
		});
	}

	//OVERRIDED FROM BASE:
	//-------------------------------------------------------------------------------------------------------
	protected createBehaviours(config: particles.ParticleConfig): void {
		console.log('createBehaviours');

		//Add the parallax position controller:
		this._position = this._behaviourManager.add(ParticleProgressParallax);

		//Mouse parallax on particles:
		this.mouseParallax = this._behaviourManager.add(ParticleMouseParallax);

		//Add orbit effect for blank hole and random ossocliation:
		this.orbitPath = this._behaviourManager.add(ParticlePath);
		this.orbitPath.path = LookupType.CIRCLE;
		this.orbitPath.randomise();

		this.iconPath = this._behaviourManager.add(ParticlePath);
		this.iconPath.path = LookupType.ICON;
		this.iconPath.randomise();

		this.mouseParallax.active = true;
		this.mouseParallax.setup({ ease: { x: 20, y: 20 }, power: { x: 0.05, y: 0.05 } });

		this.counter = this._behaviourManager.add(ParticleCounter);
		this.counter2 = this._behaviourManager.add(ParticleCounter);

		this.depth = this._behaviourManager.add(ParticleDepth);
		this.depth.mouseParallax = this.mouseParallax;

		this.hover = this._behaviourManager.add(ParticleHover);
		this.particlePhysics = this._behaviourManager.add(ParticlePhysics);

		this.repell = this._behaviourManager.add(ParticleRepell);
		this.depth.repell = this.repell;

		this.emitter = this._behaviourManager.add(ParticleTrailEmitter);
		this._trail = this._behaviourManager.add(ParticleTrail);

		//Deactivate all behaviours but the 'base' ones
		this.deactivateAllBehaviours(true);

		this._defaultState();
	}

	//PRIVATE METHODS
	//--------------------------------------------------------------------------------------------------
	private _defaultState(): void {
		this._animatedProps.targetScale = 0;
		this._animatedProps.setEase({
			x: 1,
			y: 1,
			scale: 1
		});
	}


	/** Partcile needs to move from left <> right
	 * So allow so easing on x/y
	 */
	private _startSceneSwitch(type) {
		this._animatedProps.setEase({
			x: 1,
			y: 1
		});

		this.mouseParallax.active = false;
		this._position.power = 0;

		this._animatedProps.targetAlpha = this._animatedProps.alpha = 0.9;
		this._animatedProps.targetScale = this._animatedProps.scale = 0.1;
	}


	public setupPath(target: ParticlePath, props: any): void {
		target.active = true;

		target.xMuliplier = props.x[0] + Math.random() * props.x[1];
		target.yMuliplier = props.y[0] + Math.random() * props.y[1];
		target.speed = props.speed[0] + Math.random() * props.speed[1];

		if (props.alpha) {
			target.alphaActive = true;
			target.alphaRange = { min: props.alpha[0], max: props.alpha[1] };
		} else {
			target.alphaActive = false;
		}

		if (props.scale) {
			target.scaleActive = true;
			target.scaleRange = { min: props.scale[0], max: props.scale[1] };
		} else {
			target.scaleActive = false;
		}

		target.direction = props.direction ? props.direction : 1;

		if (props.randomise) {
			this.orbitPath.randomise(props.randomise.unsync, props.randomise.direction);
		}
	}


}
